import { getArticleFormDefaultValues, useArticleInputFields } from '@buildwise/libs/posts';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { ArticleFieldsType, ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { Typography, Box, Button } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { formatDate } from 'utility/helpers';
import { useProjectRetroContext } from '../../context';
import { Link, useParams } from 'react-router-dom';
import { DataGrid, gridClasses, GridColDef, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import LessonType from './LessonType';
import AddLineIcon from 'remixicon-react/AddLineIcon';
import { compact, Dictionary, keyBy } from 'lodash';
import { ConfigEntryType } from 'config/config';
import Chat3LineIcon from 'remixicon-react/Chat3LineIcon';
import Attachment2Icon from 'remixicon-react/Attachment2Icon';
import { theme } from 'config/theme';
import Avatar from 'components/common/avatar/Avatar';
import { USER_PROFILE } from 'constants/routes';
import { singular } from 'pluralize';
import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';

type LessonsTableProps = {
  isLoading: boolean;
  lessons: ArticleType[];
};

function LessonsTable({ lessons, isLoading }: LessonsTableProps) {
  const { projectId = '' } = useParams();
  const {
    state: {
      isOpenedLessonForm,
      articleFields,
      isFullScreen,
      wrapText,
      view,
      spanRows,
      selectedArticle,
    },
    dispatch,
  } = useProjectRetroContext();
  const { articleFieldsToRender } = useArticleInputFields({
    articleFieldsType: ArticleFieldsType.LESSON,
    isEditing: false,
  });
  const enabledArticleFieldsToRender: Dictionary<ConfigEntryType | undefined> = useMemo(
    () => keyBy(articleFieldsToRender, 'entryCode'),
    [articleFieldsToRender],
  );
  const { reset } = useFormContext<PostFormData>();
  const getColumnLabel = useCallback(
    (name: string) =>
      enabledArticleFieldsToRender[name]?.referenceObject?.label ||
      enabledArticleFieldsToRender[name]?.entryDescription,
    [enabledArticleFieldsToRender],
  );
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const { companyId } = useUserData();
  const { config } = useConfigQuery({
    companyId,
    configName: CONFIGS.PROJECT_PHASES.configName,
  });
  const columns = useMemo(() => {
    const columns: GridColDef<ArticleType>[] = compact([
      { field: 'id', headerName: 'ID', width: 30, disableColumnMenu: true },
      enabledArticleFieldsToRender['lessonTypes']?.isActive && {
        field: 'lessonTypes',
        headerName: getColumnLabel('lessonTypes') || 'Lesson Type',
        width: 200,
        renderCell: ({ row }) => (
          <>
            {row?.lessonTypes?.[0] ? (
              <LessonType lessonType={row?.lessonTypes?.[0]} />
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
      },
      {
        field: 'title',
        headerName: getColumnLabel('title') || 'Title',
        width: isFullScreen ? 400 : 300,
        sortable: true,
      },
      {
        field: 'description',
        headerName: getColumnLabel('description') || 'Description',
        width: isFullScreen ? 700 : 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Typography
            dangerouslySetInnerHTML={{
              __html: row?.description || '',
            }}
            sx={{
              '& img, & table': {
                display: 'none',
              },
              ...(!wrapText && {
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: '1',
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
              }),
            }}
          />
        ),
        rowSpanValueGetter: () => null,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: 150,
        renderCell: ({ row }) => <>{formatDate(row.createdAt)}</>,
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        width: 200,
        renderCell: ({ row }) => (
          <Link to={USER_PROFILE.replace(':userName', row?.createdBy || '')}>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Avatar
                src={row?.profileImageURL}
                size={28}
                fontSize={14}
                name={row.userNameToDisplay}
              />{' '}
              <Typography
                sx={(theme) => ({
                  color: theme.palette.custom.tuna,
                  fontSize: 14,
                  fontWeight: 500,
                })}
              >
                {row?.userNameToDisplay}
              </Typography>
            </Box>
          </Link>
        ),
        rowSpanValueGetter: () => null,
      },
      {
        field: 'totalComments',
        headerName: 'Comments',
        width: 150,
        renderCell: ({ row }) =>
          row.totalComments > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Chat3LineIcon size={18} color={theme.palette.custom.tuna} /> {row.totalComments}
            </Box>
          ),
      },
      {
        field: 'attachments',
        headerName: 'Attachments',
        width: 200,
        renderCell: ({ row }) =>
          row.attachments &&
          row.attachments?.length > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Attachment2Icon size={18} color={theme.palette.custom.tuna} />{' '}
              {row.attachments?.length}
            </Box>
          ),
        rowSpanValueGetter: () => null,
        sortComparator: (v1, v2) => (v1?.length || 0) - (v2?.length || 0),
      },
    ]);

    if (view === 'TABLE') {
      columns.splice(
        1,
        0,
        ...[
          {
            field: 'projectPhases',
            headerName: singular(config?.description || 'Phase'),
            width: 150,
            valueGetter: (value) => (Array.isArray(value) ? value.join(', ') || '' : ''),
          },
        ],
      );
    }

    return columns;
  }, [view, enabledArticleFieldsToRender, getColumnLabel, wrapText]);

  useEffect(() => {
    const updateHeight = () => {
      if (view === 'TABLE' && tableContainerRef.current) {
        // Get the container's top position relative to the viewport
        const containerTop = tableContainerRef.current.getBoundingClientRect().top;
        // Calculate height from its current position to the bottom of the viewport
        if (containerTop <= 200) {
          tableContainerRef.current.style.height = `${window.innerHeight - containerTop - (isFullScreen ? 0 : 16)}px`;
        }
      }
    };

    updateHeight();

    window.addEventListener('resize', updateHeight);
    window.addEventListener('scroll', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('scroll', updateHeight);
    };
  }, [tableContainerRef.current, lessons, isFullScreen, view]);

  return (
    <Box ref={tableContainerRef}>
      <DataGrid
        loading={isLoading}
        columns={columns}
        rows={lessons}
        checkboxSelection={false}
        hideFooter={lessons.length <= 100}
        columnVisibilityModel={{ id: false }}
        {...(spanRows && {
          unstable_rowSpanning: true,
        })}
        {...(view === 'TABLE' && {
          showCellVerticalBorder: true,
          showColumnVerticalBorder: true,
          slots: {
            toolbar: () => (
              <Box display="flex" alignItems="center" justifyContent="flex-end" p={0.5} px={1}>
                <GridToolbarQuickFilter
                  sx={{
                    width: 400,
                    py: 0.5,
                  }}
                />
              </Box>
            ),
          },
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              quickFilterProps: { debounceMs: 500 },
            },
          },
        })}
        onRowClick={({ row }) => {
          reset({
            ...getArticleFormDefaultValues({
              article: row,
              defaultProjectId: projectId,
              articleFields,
            }),
            projectPhases: row?.projectPhases || [],
          });
          dispatch(({ state }) => ({
            payload: {
              ...state,
              selectedArticle: row,
              articleIds: [],
            },
          }));
        }}
        onCellKeyDown={(params, event) => {
          let article: ArticleType | undefined;

          if (event.code === 'ArrowDown') {
            const articleIndex = lessons.findIndex((lesson) => lesson.id === params.row.id);
            if (articleIndex > -1) {
              article = lessons[articleIndex + 1];
            } else {
              article = lessons[0];
            }
          }

          if (event.code === 'ArrowUp') {
            const articleIndex = lessons.findIndex((lesson) => lesson.id === params.row.id);
            if (articleIndex > -1) {
              article = lessons[articleIndex - 1];
            }
          }

          if (article) {
            reset({
              ...getArticleFormDefaultValues({
                article: article,
                defaultProjectId: projectId,
                articleFields,
              }),
              projectPhases: article.projectPhases || [],
            });
            dispatch(({ state }) => ({
              payload: {
                ...state,
                selectedArticle: article,
                articleIds: [],
              },
            }));
          }
        }}
        rowSelectionModel={selectedArticle?.id ? [selectedArticle?.id] : []}
        getRowHeight={() => (wrapText ? 'auto' : undefined)}
        sx={(theme) => ({
          '& .MuiDataGrid-scrollbar': {
            visibility: 'hidden',
          },
          '&:hover': {
            '& .MuiDataGrid-scrollbar': {
              visibility: 'visible',
            },
          },
          [`& .${gridClasses.cell}`]: {
            py: wrapText ? 1.5 : 1,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },
          '& .MuiDataGrid-row--dynamicHeight': {
            '& [data-colindex]': {
              borderRight: `1px solid ${theme.palette.custom.pearlBush}`,
            },
          },
        })}
      />
    </Box>
  );
}

export default LessonsTable;
